import React, {useEffect, useState} from "react";
import {useLocation, useNavigate, useParams} from "react-router-dom";
import moment from "moment";

import Loading from "@/components/Loading";
import Page from "@/components/Page";
import Breadcrumbs from "@/components/Breadcrumbs";
import EmptyData from "@/components/EmptyData";
import MainActions from "@/components/MainActions";
import VLFCard from "@/components/Card";
import ViewField from "@/components/Input/ViewField";
import VLFModal from "@/components/Modal";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";

import {ScoutingReportAPI} from "@/api/scouting_report";

import {
	ERROR_OPERATION,
	SUCCESSFUL_OPERATION,
	dateFormat,
	matchTypesOptions,
} from "@/utils/constants";
import {getAlertSeverity, getValuationType} from "@/utils/functions";

import "../Valuation/index.scss";

function ScoutValuation(props) {
	const {handleNotification, external} = props;

	const playerId = new URLSearchParams(useLocation().search).get("playerId");

	const {id} = useParams();
	const navigate = useNavigate();

	const [loading, setLoading] = useState(false);
	const [openHandleConfirm, setOpenHandleConfirm] = useState(false);
	const [valuationData, setValuationData] = useState({});

	const getValuation = async () => {
		setLoading(true);
		try {
			const response = await ScoutingReportAPI.getById(id);
			setValuationData(response);
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};
	useEffect(() => {
		getValuation();
	}, []);

	const deleteValuation = async () => {
		setLoading(true);
		try {
			await ScoutingReportAPI.delete(valuationData?.id);
			handleNotification(SUCCESSFUL_OPERATION, "success");
			navigate(external ? "/scouting/valuations" : "/players/" + playerId);
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
			setLoading(false);
		} finally {
			setOpenHandleConfirm(false);
		}
	};

	const reportConfigurationSize = "col-6 col-xl-3";
	const reportConfiguration = [
		{
			value: valuationData?.reportDate
				? moment(valuationData?.reportDate).format(dateFormat)
				: null,
			label: "Data",
			size: reportConfigurationSize,
		},
		{
			value: valuationData?.scout,
			label: "Relatore",
			size: reportConfigurationSize,
		},
	];

	if (external) {
		reportConfiguration.unshift({
			value: getValuationType(valuationData.valuationType),
			label: "Tipologia",
			size: reportConfigurationSize,
		});
	}

	const currentPlayerData = valuationData?.player;
	if (external) {
		reportConfiguration.push({
			value: valuationData?.playerClub?.name,
			label: "Squadra",
			size: reportConfigurationSize,
		});

		reportConfiguration.push({
			value: valuationData?.playerCategory?.name,
			label: "Categoria",
			size: reportConfigurationSize,
		});
	} else {
		reportConfiguration.push({
			value: valuationData?.momentOfGrowth,
			label: "Momento di crescita",
			size: reportConfigurationSize,
		});
	}

	reportConfiguration.push(
		{
			value: valuationData?.opposingTeam,
			label: "Squadra avversaria",
			size: reportConfigurationSize,
		},
		{
			value: matchTypesOptions?.filter(
				(mo) => mo.ordinal === valuationData?.matchType,
			)?.[0]?.label,
			label: "Tipo di partita",
			size: reportConfigurationSize,
		},
		{
			value: valuationData?.observedPerformanceModel,
			label: "Modello prestativo",
			size: reportConfigurationSize,
		},
	);

	const playerReportConfigurationSize = "col-6 col-xl-3";
	const playerReportConfiguration = [
		{
			value: currentPlayerData
				? currentPlayerData?.firstName + " " + currentPlayerData?.lastName
				: "---",
			label: "Giocatore",
			size: playerReportConfigurationSize,
		},
		{
			value: !external
				? currentPlayerData?.category
				: currentPlayerData?.dateOfBirth
					? moment(currentPlayerData?.dateOfBirth).format(dateFormat)
					: "---",
			label: !external ? "Categoria" : "Data di nascita",
			size: playerReportConfigurationSize,
		},
		{
			value: currentPlayerData?.goalkeeper
				? "Portiere"
				: currentPlayerData?.primaryRole,
			label: "Ruolo",
			size: playerReportConfigurationSize,
		},
		{
			value: valuationData?.favoriteFoot,
			label: "Piede principale",
			size: playerReportConfigurationSize,
		},
		{
			value: valuationData?.playerPrevalentAttitude,
			label: "Attitudine prevalente",
			size: playerReportConfigurationSize,
		},
		{
			value: valuationData?.playerCity,
			label: "Città",
			size: playerReportConfigurationSize,
		},
		{
			value: valuationData?.playerDistrict,
			label: "Provincia",
			size: playerReportConfigurationSize,
		},
	];

	if (external) {
		// playerReportConfiguration.push({
		// 	value: valuationData?.trialPeriod,
		// 	label: "Periodo di prova",
		// 	size: playerReportConfigurationSize,
		// });
	} else {
		playerReportConfiguration.push({
			value: valuationData?.timeInsideClub,
			label: "Nel club dal",
			size: playerReportConfigurationSize,
		});
	}

	const reportDetailsConfiguration = [
		{
			value: (
				<div className="area-rating-container">
					<div className="rating-notes main">
						{valuationData?.physicalCharacteristicsType || "---"}
					</div>
					<div className="rating-notes">
						{valuationData?.physicalCharacteristicsDescription}
					</div>
					{!valuationData?.physicalCharacteristicsType &&
						!valuationData?.physicalCharacteristicsDescription && <EmptyData />}
				</div>
			),
			label: "Caratteristiche fisiche",
			size: "col-12 col-sm-6",
		},
		{
			value: (
				<div className="area-rating-container">
					<div className="rating-notes main">
						{valuationData?.psychologicalCharacteristicsType || "---"}
					</div>
					<div className="rating-notes">
						{valuationData?.psychologicalCharacteristicsDescription}
					</div>
					{!valuationData?.psychologicalCharacteristicsType &&
						!valuationData?.psychologicalCharacteristicsDescription && (
							<EmptyData />
						)}
				</div>
			),
			label: "Caratteristiche psicologiche",
			size: "col-12 col-sm-6",
		},
		{
			value: (
				<div className="area-rating-container only-text">
					{valuationData?.athleticQualities ? (
						<div className="rating-notes">
							{valuationData?.athleticQualities}
						</div>
					) : (
						<EmptyData />
					)}
				</div>
			),
			label: "Qualità atletiche",
			size: "col-12 col-sm-6",
		},
		{
			value: (
				<div className="area-rating-container only-text">
					{valuationData?.coordinationQualities ? (
						<div className="rating-notes">
							{valuationData?.coordinationQualities}
						</div>
					) : (
						<EmptyData />
					)}
				</div>
			),
			label: "Qualità coordinative",
			size: "col-12 col-sm-6",
		},
		{
			value: (
				<div className="area-rating-container only-text">
					{valuationData?.structuralQualities ? (
						<div className="rating-notes">
							{valuationData?.structuralQualities}
						</div>
					) : (
						<EmptyData />
					)}
				</div>
			),
			label: "Qualità strutturali",
			size: "col-12 col-sm-6",
		},
		{
			value: (
				<div className="area-rating-container only-text">
					{valuationData?.technicalQualities ? (
						<div className="rating-notes">
							{valuationData?.technicalQualities}
						</div>
					) : (
						<EmptyData />
					)}
				</div>
			),
			label: "Qualità tecniche",
			size: "col-12 col-sm-6",
		},
		{
			value: (
				<div className="area-rating-container only-text">
					{valuationData?.tacticalQualities ? (
						<div className="rating-notes">
							{valuationData?.tacticalQualities}
						</div>
					) : (
						<EmptyData />
					)}
				</div>
			),
			label: "Qualità tattiche",
			size: "col-12 col-sm-6",
		},

		{
			value: (
				<div className="area-rating-container only-text">
					{valuationData?.finalOverallValuation ? (
						<div className="rating-notes">
							{valuationData?.finalOverallValuation}
						</div>
					) : (
						<EmptyData />
					)}
				</div>
			),
			label: "Giudizio generale",
			size: "col-12 col-sm-6",
		},
		{
			value: (
				<div className="area-rating-container only-text">
					{valuationData?.marginsImprovement ? (
						<div className="rating-notes">
							{valuationData?.marginsImprovement}
						</div>
					) : (
						<EmptyData />
					)}
				</div>
			),
			label: "Margini di miglioramento",
			size: "col-12 col-sm-6",
		},
		{
			value: (
				<div className="area-rating-container only-text">
					{valuationData?.internalCommunications ? (
						<div className="rating-notes">
							{valuationData?.internalCommunications}
						</div>
					) : (
						<EmptyData />
					)}
				</div>
			),
			label: "Eventuali comunicazioni interne",
			size: "col-12 col-sm-6",
		},
	];

	const mainActions = [
		{
			icon: <ArrowBackIcon />,
			title: "Indietro",
			onClick: () => navigate(-1),
			color: "info",
		},
		{
			icon: <EditIcon />,
			title: "Modifica",
			onClick: () =>
				navigate(
					external
						? "/scouting/valuations/" + valuationData?.id + "/edit?type=scout"
						: "/players-valuations/" +
								valuationData?.id +
								"/edit?type=scout&playerId=" +
								playerId,
				),
		},
		{
			icon: <DeleteIcon />,
			title: "Elimina",
			onClick: () => setOpenHandleConfirm(true),
			color: "error",
		},
	];

	const breadcrumbsElements = [
		{
			label: "Valutazioni",
			path: external ? "/scouting/valuations" : "/players/" + playerId,
		},
		{label: "/"},
	];

	return (
		<Page className="page-container">
			<Loading visible={loading} />
			<Breadcrumbs
				elements={breadcrumbsElements}
				current={
					valuationData
						? moment(valuationData?.reportDate).format(dateFormat) +
							" " +
							(currentPlayerData?.firstName || "") +
							" " +
							(currentPlayerData?.lastName || "")
						: ""
				}
				actions={<MainActions actions={mainActions} />}
			/>
			<div className="sheet-container">
				<div className="row no-margin col-container">
					<div className="col-sm-12 col-lg-12 padding-small">
						<VLFCard
							title="Informazioni valutazione"
							content={
								<div className="row">
									<div className="col-sm-12 col-lg-10">
										<ViewField configuration={reportConfiguration} />
									</div>
									<div className="col-sm-12 col-lg-2">
										<div
											className="d-flex flex-column align-items-center gap-2 h-100"
											style={{borderLeft: "1px solid #ebf1fd"}}
										>
											{valuationData.valuationType !== 6 ? (
												<div className="view-field-container avg h-100 justify-content-center">
													<div className="field-label-container justify-content-center">
														<div className="field-label">Voto finale</div>
													</div>
													<div className="stat-circle-container">
														<div className="stat-container">
															<div className="value">
																{
																	+parseFloat(
																		valuationData?.finalAverage || 0,
																	).toFixed(2)
																}
															</div>
														</div>
													</div>
												</div>
											) : null}
											{external ? (
												<div className="view-field-container avg h-100 justify-content-center">
													<div className="field-label-container justify-content-center">
														<div className="field-label">Esito</div>
													</div>
													<div className="d-flex justify-content-center">
														<div
															style={{maxWidth: "150px"}}
															className={
																"severity " +
																(getAlertSeverity(
																	valuationData?.valuation,
																	true,
																)?.severity || "")
															}
														>
															{
																getAlertSeverity(valuationData?.valuation, true)
																	?.message
															}
														</div>
													</div>
												</div>
											) : null}
										</div>
									</div>
								</div>
							}
							openable={false}
						/>
					</div>
				</div>
				<div className="row no-margin mt-2">
					<div className="col-sm-12 padding-small">
						<VLFCard
							title="Informazioni giocatore"
							content={
								<div className="row">
									<div className="col-sm-12">
										<ViewField configuration={playerReportConfiguration} />
									</div>
								</div>
							}
						/>
					</div>
				</div>
				<div className="row no-margin mt-2">
					<div className="col-sm-12 padding-small">
						<VLFCard
							title="Dettagli"
							content={
								<div className="row">
									<div className="col-sm-12">
										<ViewField configuration={reportDetailsConfiguration} />
									</div>
								</div>
							}
						/>
					</div>
				</div>
			</div>
			<VLFModal
				open={openHandleConfirm}
				title="Eliminazione"
				content={
					<div className="mt-2">
						Sei sicuro di voler eliminare questo elemento?
					</div>
				}
				confirm={() => deleteValuation()}
				close={() => setOpenHandleConfirm(false)}
				confirmLabel="Conferma"
			/>
		</Page>
	);
}

export default ScoutValuation;
